import { create } from "zustand";
import { auth, onAuthStateChanged } from '../../../../auth';
import { get as idbGet, set as idbSet, del as idbDel } from "idb-keyval";
import convex, { waitForConvexAuthReady } from "../../../../convexClient";
import { api } from "../../../../convex/_generated/api";
import {
  getActiveTenantId,
  withActiveTenant,
} from "../../../../utils/tenantUtils";

// Three typed catalogs are tenant-scoped (shared across all tenant members).
// accountsGroups is global — any signed-in user can edit.
const COLLECTION_TO_API = {
  expenseGroups: api.categories.listExpenseGroups,
  incomeGroups: api.categories.listIncomeGroups,
  savingsGroups: api.categories.listSavingsGroups,
  accountsGroups: api.categories.listAccountsGroups,
};

const TENANT_SCOPED_COLLECTIONS = new Set([
  "expenseGroups",
  "incomeGroups",
  "savingsGroups",
]);

// Mirrors the Convex `createCategoryGroup` validator. Strict-mode args fail the
// mutation if callers pass legacy keys (notably the Phase 7 `scope` field).
const CREATE_GROUP_ALLOWED_FIELDS = [
  "name",
  "icon",
  "description",
  "categoryType",
  "categoryNature",
];

function pickCreateGroupPayload(data) {
  const out = {};
  for (const key of CREATE_GROUP_ALLOWED_FIELDS) {
    if (data[key] !== undefined) out[key] = data[key];
  }
  return out;
}

let authReadyPromise = null;
function ensureUser() {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  if (!authReadyPromise) {
    authReadyPromise = new Promise((resolve) => {
      const unsub = onAuthStateChanged(auth, (user) => {
        unsub();
        resolve(user || null);
      });
    });
  }
  return authReadyPromise;
}

const buildTenantCacheKey = (name, tenantId) =>
  `${name}:${tenantId || "global"}`;

const normalizeUnsubscribers = (entry) =>
  (Array.isArray(entry) ? entry : [entry]).filter(
    (unsubscribe) => typeof unsubscribe === "function"
  );

export const useGroupStore = create((set, get) => ({
  data: {},
  cache: {},
  offlineQueue: [],
  activeGroups: [],
  loading: false,
  error: null,
  success: null,
  // Convex watchQuery unsubscribe handles, keyed by collection name
  unsubscribers: {},

  _saveToIDB: async (key, data) => {
    try { await idbSet(key, data); } catch (err) { console.warn("IDB save failed:", err); }
  },

  _loadFromIDB: async (key) => {
    try { return (await idbGet(key)) || []; } catch { return []; }
  },

  _queueOfflineAction: (action) => {
    const queue = [...get().offlineQueue, action];
    set({ offlineQueue: queue });
    get()._saveToIDB("offlineQueue", queue);
  },

  _flushOfflineQueue: async () => {
    const queue = await get()._loadFromIDB("offlineQueue");
    if (!queue.length) return;
    const { createDocument, updateDocument, deleteDocument } = get();
    for (const item of queue) {
      try {
        if (item.type === "create") await createDocument(item.collection, item.data, { fromQueue: true });
        else if (item.type === "update") await updateDocument(item.collection, item.id, item.data, { fromQueue: true });
        else if (item.type === "delete") await deleteDocument(item.collection, item.id, { fromQueue: true });
      } catch (err) {
        console.error("Failed syncing queued item:", err);
      }
    }
    set({ offlineQueue: [] });
    await idbDel("offlineQueue");
  },

  _resolveConflict: (localDoc, remoteDoc) => {
    const localUpdated = localDoc.lastUpdated || 0;
    const remoteUpdated = remoteDoc.lastUpdated || 0;
    return localUpdated > remoteUpdated ? localDoc : remoteDoc;
  },

  _registerUnsubscriber: (name, unsubscribe) => {
    if (typeof unsubscribe !== "function") return null;

    let disposed = false;
    const wrapped = () => {
      if (disposed) return;
      disposed = true;

      try {
        unsubscribe();
      } catch (error) {
        console.warn(`[useGroupStore] Failed to unsubscribe ${name}:`, error);
      } finally {
        const current = get().unsubscribers[name];
        if (current === wrapped) {
          const nextUnsubscribers = { ...get().unsubscribers };
          delete nextUnsubscribers[name];
          set({ unsubscribers: nextUnsubscribers });
        }
      }
    };

    set((state) => ({
      unsubscribers: {
        ...state.unsubscribers,
        [name]: wrapped,
      },
    }));

    return wrapped;
  },

  _consumeUnsubscriber: (name) => {
    const entry = get().unsubscribers[name];
    if (!entry) return;

    normalizeUnsubscribers(entry).forEach((unsubscribe) => unsubscribe());

    if (get().unsubscribers[name] === entry) {
      const nextUnsubscribers = { ...get().unsubscribers };
      delete nextUnsubscribers[name];
      set({ unsubscribers: nextUnsubscribers });
    }
  },

  /* ─── Fetch Collection(s) ───────────────────────────────────────────── */
  fetchCollection: async (collectionNames, { realtime = true, useCache = true, force = false } = {}) => {
    const {
      cache,
      unsubscribers,
      _saveToIDB,
      _loadFromIDB,
      _consumeUnsubscriber,
      _registerUnsubscriber,
    } = get();
    const user = await ensureUser();
    if (!user) {
      set({ loading: false, error: null });
      return;
    }

    await waitForConvexAuthReady();

    const tenantId = getActiveTenantId();
    const names = Array.isArray(collectionNames) ? collectionNames : [collectionNames];

    // Phase 7: tenant-scoped tables need a tenantId; the global accountsGroups
    // catalog does not. Only block when at least one tenant-scoped table is
    // requested.
    const needsTenant = names.some((n) => TENANT_SCOPED_COLLECTIONS.has(n));
    if (needsTenant && !tenantId) {
      set({ loading: false, error: "No active workspace selected." });
      return;
    }

    set({ loading: true, error: null });

    try {
      for (const name of names) {
        // If already subscribed and not forcing, skip
        if (realtime && unsubscribers[name] && !force) {
          if (useCache && cache[name]) {
            set((s) => ({ data: { ...s.data, [name]: cache[name] }, loading: false }));
          }
          continue;
        }

        // Load offline data immediately while subscription connects
        const cacheKey = buildTenantCacheKey(name, tenantId);
        const offlineData = await _loadFromIDB(cacheKey);
        if (offlineData.length > 0) {
          set((s) => ({ data: { ...s.data, [name]: offlineData } }));
        }

        // Tear down any existing subscription
        _consumeUnsubscriber(name);

        const queryApi = COLLECTION_TO_API[name];
        if (!queryApi) {
          console.warn(`[useGroupStore] Unknown collection: ${name}`);
          continue;
        }

        const queryArgs = TENANT_SCOPED_COLLECTIONS.has(name) ? { tenantId } : {};

        if (realtime) {
          const watch = convex.watchQuery(queryApi, queryArgs);
          const unsub = watch.onUpdate(async () => {
            let result;
            try {
              result = watch.localQueryResult();
            } catch (err) {
              console.error("[useGroupStore] localQueryResult error:", err);
              return;
            }
            if (result === undefined) return;

            const local = get().data[name] || [];
            const merged = result.map((remote) => {
              const localDoc = local.find((l) => l.id === remote._id || l.id === remote.id);
              return localDoc ? get()._resolveConflict(localDoc, { ...remote, id: remote._id }) : { ...remote, id: remote._id };
            });

            get().cache[name] = merged;
            set((s) => ({ data: { ...s.data, [name]: merged }, loading: false }));
            await _saveToIDB(cacheKey, merged);
          });

          _registerUnsubscriber(name, unsub);
        } else {
          if (useCache && cache[name] && !force) {
            set((s) => ({ data: { ...s.data, [name]: cache[name] }, loading: false }));
            continue;
          }
          const docs = await convex.query(queryApi, queryArgs);
          const normalized = docs.map((d) => ({ ...d, id: d._id, _collection: name }));
          get().cache[name] = normalized;
          set((s) => ({ data: { ...s.data, [name]: normalized }, loading: false }));
          await _saveToIDB(cacheKey, normalized);
        }
      }

      // Merge all fetched groups into convenience `groups` key
      const combinedGroups = names.flatMap((n) => get().data[n] || []);
      set((s) => ({ data: { ...s.data, groups: combinedGroups }, loading: false }));
      await get()._saveToIDB(buildTenantCacheKey("groups", tenantId), combinedGroups);
    } catch (error) {
      console.error("Error fetching collections:", error);
      set({ error: error.message, loading: false });
    }
  },

  /* ─── CRUD ────────────────────────────────────────────────────────────── */
  createDocument: async (collectionName, data, opts = {}) => {
    const { _queueOfflineAction } = get();
    const user = await ensureUser();
    if (!user) {
      set({ error: "User not authenticated" });
      return;
    }
    try {
      const collection = collectionName;
      if (!navigator.onLine && !opts.fromQueue) {
        _queueOfflineAction({ type: "create", collection, data });
        return null;
      }

      // Phase 7: scope/sharing on category groups is deprecated; tenantId only
      // applies to the three typed tables. accountsGroups is global. Whitelist
      // to the validator-allowed fields so legacy callers passing `scope` (or
      // any other dead field) do not trip Convex strict-mode arg validation.
      const cleanData = pickCreateGroupPayload(data);
      const payload = TENANT_SCOPED_COLLECTIONS.has(collection)
        ? withActiveTenant(cleanData)
        : cleanData;

      const id = await convex.mutation(api.categories.createCategoryGroup, {
        collection,
        ...payload,
      });

      // Don't manually update state here — the watchQuery subscription will
      // fire from the mutation and update the list. A manual addition would
      // duplicate the item before the subscription merges.
      set({ success: `${data.name || "Item"} created successfully` });
      return id;
    } catch (error) {
      console.error("Error creating document:", error);
      set({ error: error.message });
      return null;
    }
  },

  updateDocument: async (collectionName, docId, updates, opts = {}) => {
    const { cache, _saveToIDB, _queueOfflineAction } = get();
    const user = await ensureUser();
    if (!user) {
      set({ error: "User not authenticated" });
      return;
    }
    try {
      const collection = collectionName;
      if (!navigator.onLine && !opts.fromQueue) {
        _queueOfflineAction({ type: "update", collection, id: docId, data: updates });
        return;
      }

      const payload = TENANT_SCOPED_COLLECTIONS.has(collection)
        ? withActiveTenant(updates)
        : { ...updates };

      await convex.mutation(api.categories.updateCategoryGroup, {
        collection,
        id: docId,
        ...payload,
      });

      const updated = (cache[collection] || []).map((d) =>
        d.id === docId ? { ...d, ...updates } : d
      );
      cache[collection] = updated;
      set((s) => ({
        data: { ...s.data, [collection]: updated },
        success: `${updates.name || "Item"} updated successfully`,
      }));
      await _saveToIDB(
        buildTenantCacheKey(collection, getActiveTenantId()),
        updated
      );
    } catch (error) {
      console.error("Error updating document:", error);
      set({ error: error.message });
    }
  },

  /**
   * Phase 7: prefer archive over delete. Falls back to hard-delete only when
   * the caller asks for it (e.g. cleanup tooling). The backend will refuse to
   * delete any group still referenced by a budget/transaction/plan/goal/account.
   */
  archiveDocument: async (collectionName, docId) => {
    const user = await ensureUser();
    if (!user) {
      set({ error: "User not authenticated" });
      return;
    }
    const collection = collectionName;
    try {
      const args = { collection, id: docId };
      if (TENANT_SCOPED_COLLECTIONS.has(collection)) {
        args.tenantId = getActiveTenantId();
      }
      await convex.mutation(api.categories.archiveCategoryGroup, args);
      set({ success: "Item archived" });
    } catch (error) {
      console.error("Error archiving document:", error);
      set({ error: error.message });
    }
  },

  unarchiveDocument: async (collectionName, docId) => {
    const user = await ensureUser();
    if (!user) {
      set({ error: "User not authenticated" });
      return;
    }
    const collection = collectionName;
    try {
      const args = { collection, id: docId };
      if (TENANT_SCOPED_COLLECTIONS.has(collection)) {
        args.tenantId = getActiveTenantId();
      }
      await convex.mutation(api.categories.unarchiveCategoryGroup, args);
      set({ success: "Item restored" });
    } catch (error) {
      console.error("Error restoring document:", error);
      set({ error: error.message });
    }
  },

  deleteDocument: async (collectionName, docId, opts = {}) => {
    const { cache, _saveToIDB, _queueOfflineAction } = get();
    const user = await ensureUser();
    if (!user) {
      set({ error: "User not authenticated" });
      return;
    }
    try {
      const collection = collectionName;
      if (!navigator.onLine && !opts.fromQueue) {
        _queueOfflineAction({ type: "delete", collection, id: docId });
        return;
      }

      const args = { collection, id: docId };
      if (TENANT_SCOPED_COLLECTIONS.has(collection)) {
        args.tenantId = getActiveTenantId();
      }
      await convex.mutation(api.categories.deleteCategoryGroup, args);

      const updated = (cache[collection] || []).filter((d) => d.id !== docId);
      cache[collection] = updated;
      set((s) => ({
        data: { ...s.data, [collection]: updated },
        success: "Item deleted successfully",
      }));
      await _saveToIDB(
        buildTenantCacheKey(collection, getActiveTenantId()),
        updated
      );
    } catch (error) {
      console.error("Error deleting document:", error);
      set({ error: error.message });
    }
  },

  createGroup: async (collectionName, data) => get().createDocument(collectionName, data),
  updateGroup: async (collectionName, id, data) => get().updateDocument(collectionName, id, data),

  /* ─── Selectors ─────────────────────────────────────────────────────── */
  getGroupsByType: (type) => (get().data.groups || []).filter((g) => g.categoryType === type),
  getGroupsByNature: (nature) => (get().data.groups || []).filter((g) => g.categoryNature === nature),

  /* ─── Lifecycle ─────────────────────────────────────────────────────── */
  clearMessages: () => set({ error: null, success: null }),

  unsubscribeAll: () => {
    const { unsubscribers } = get();
    Object.values(unsubscribers).forEach((entry) => {
      normalizeUnsubscribers(entry).forEach((unsubscribe) => unsubscribe());
    });
    set({ unsubscribers: {} });
  },

  initializeOfflineSupport: async () => {
    window.addEventListener("online", get()._flushOfflineQueue);
    const queued = await get()._loadFromIDB("offlineQueue");
    if (queued.length > 0) set({ offlineQueue: queued });
  },
}));
